import { cva, type VariantProps } from 'class-variance-authority'
import { Hide } from 'grommet-icons'
import React from 'react'
import { useFormContext } from 'react-hook-form'
import { EyeIcon } from '../../../assets/svgs'
import { cn } from '../../../utils/ClassName'

const containerStyles = cva('flex items-center overflow-hidden border transition-colors w-full', {
  defaultVariants: {
    intent: 'default',
    rounded: 'pill',
    size: 'md'
  },
  variants: {
    intent: {
      default: 'border-black/30 bg-white',
      subtle: 'border-[#E2E4E5] bg-[#F8F8F8]',
      danger: 'border-rose-500 bg-white'
    },
    rounded: {
      md: 'rounded-md',
      lg: 'rounded-xl',
      pill: 'rounded-full'
    },
    size: {
      sm: 'h-9',
      md: 'h-11',
      lg: 'h-12'
    }
  }
})

const inputStyles = cva('w-full h-full bg-transparent px-4 text-sm outline-none placeholder:text-gray-400')

interface IButtonProps extends VariantProps<typeof containerStyles> {
  placeholder: string
  errorMessage?: string
  name: string
  style?: React.CSSProperties
  className?: string
  inputClassName?: string
}

export const PasswordInputField = ({
  placeholder,
  name,
  style,
  errorMessage,
  className,
  inputClassName,
  intent,
  rounded,
  size,
  ...rest
}: IButtonProps) => {
  const [reveal, setReveal] = React.useState(false)
  const {
    register,
    formState: { errors }
  } = useFormContext()

  const errorText = errors[name]?.message
  const displayError = errorText && typeof errorText === 'string' ? errorText : errorMessage

  return (
    <>
      <div className={cn(containerStyles({ intent, rounded, size }), className)} style={style}>
        <input
          aria-label="Input Password"
          placeholder={placeholder}
          type={reveal ? 'text' : 'password'}
          {...register(name)}
          {...rest}
          className={cn(inputStyles(), inputClassName)}
        />
        <button
          aria-label={reveal ? 'Hide password' : 'Show password'}
          className="pr-3 text-[#6B7280] hover:text-[#1F2937] transition-colors"
          onClick={() => setReveal(!reveal)}
          type="button"
        >
          {reveal ? <EyeIcon /> : <Hide size="medium" />}
        </button>
      </div>
      {displayError && <p className="pl-3 mt-1 text-sm text-rose-600">{displayError}</p>}
    </>
  )
}
